/**
 * Modal de promoción del webinar "El gran reset del Marketing en LATAM".
 *
 * Campaña temporal (julio 2026): se promociona hasta el inicio del evento
 * (jueves 23 de julio, 10:00 a.m. Colombia). Todo el control es de frontend
 * (no requiere DB): la persistencia del descarte vive en localStorage, por lo
 * que aplica por navegador/dispositivo.
 *
 * Comportamiento (solo admins, y solo en empresas no excluidas — ver
 * `excludedCompanies.constants.ts`):
 *  - Clic en el CTA "¡Regístrate Ahora!" → descarte permanente (ya actuó).
 *  - Aparece **una vez cada 7 días por persona** (ADV-3347, regla de toda la
 *    familia: ver `specialModalFrequency.constants.ts`). Cerrar (X) o "Más
 *    tarde" solo cierran la vista: la ventana ya se abrió al mostrarlo.
 *
 * Hasta ADV-3347 cerrar posponía el modal 1 hora (`webinar_reset_snooze_v2`,
 * por navegador). Esa marca ya no se lee ni se escribe.
 *
 * Nota de versionado: la clave de descarte lleva sufijo `_v2`. La primera
 * versión usaba `webinar_reset_dismissed`, donde la X descartaba de forma
 * permanente. Al cambiar esa regla, renombramos la clave para IGNORAR los flags
 * viejos y reenganchar a los admins que ya lo habían descartado.
 */

import { isExcludedFromSpecialModals } from 'constants/excludedCompanies.constants';
import {
  SpecialModalUserId,
  wasSpecialModalShownRecently
} from 'constants/specialModalFrequency.constants';

/** Corte de visibilidad: inicio del evento (10:00 a.m. UTC-5 Colombia). */
export const WEBINAR_MODAL_END_DATE = '2026-07-23T10:00:00-05:00';

/** URL de registro al webinar (con UTMs de la campaña). */
export const WEBINAR_REGISTER_URL =
  'https://magneticsuite.com/webinar-gran-reset-cs?utm_campaign=47216180-%5BWebinar%5D%20El%20gran%20Reset%20del%20marketing%20en%20LATAM&utm_source=email&utm_medium=webinar%20Reset%20del%20marketing&utm_term=webinar%20Reset%20del%20marketing&utm_content=Email%2001%20CS%20webinar';

/** Descarte permanente (solo el CTA de registro). */
export const WEBINAR_DISMISSED_KEY = 'webinar_reset_done_v2';

/** Key de este modal para la marca de frecuencia de la familia. */
export const WEBINAR_MODAL_KEY = 'webinar_reset_v2';

/**
 * Decide si el modal del webinar debe mostrarse: campaña vigente + empresa no
 * excluida + no descartado permanentemente en este navegador + ventana de
 * frecuencia vencida para esta persona.
 */
export const shouldShowWebinarModal = (userId: SpecialModalUserId): boolean => {
  // Sin id no hay forma de recordar la última aparición: mostrarlo
  // significaría reaparecer en cada carga.
  if (userId === undefined || userId === null || userId === '') return false;
  if (Date.now() >= new Date(WEBINAR_MODAL_END_DATE).getTime()) return false;
  if (isExcludedFromSpecialModals()) return false;
  try {
    if (localStorage.getItem(WEBINAR_DISMISSED_KEY) === 'true') return false;
  } catch {
    // localStorage inaccesible (modo privado restrictivo): mejor no mostrarlo
    // que mostrarlo en cada carga sin poder recordar el descarte.
    return false;
  }
  if (wasSpecialModalShownRecently(WEBINAR_MODAL_KEY, userId)) return false;
  return true;
};

/** Persiste el descarte permanente (solo el CTA de registro). */
export const dismissWebinarModal = (): void => {
  try {
    localStorage.setItem(WEBINAR_DISMISSED_KEY, 'true');
  } catch {
    // Sin almacenamiento disponible no hay nada que persistir.
  }
};
